import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  type GridToolbarProps,
} from "@mui/x-data-grid-premium";
import { alpha } from "@mui/material/styles";
import { useBrandThemeColors } from "../../branding";
import { useSnackbar } from "../../contexts/SnackbarContext";

const OBJECT_ID_SEARCH_DEBOUNCE_MS = 300;

type AiHubTableToolbarProps = GridToolbarProps & {
  title?: string;
  exportTooltip?: string;
  emptyExportTooltip?: string;
  hasExportSelection?: boolean;
  exportFailureMessage?: string;
  onExport?: () => void | Promise<void>;
  showExport?: boolean;
  objectIdSearch?: string;
  onObjectIdSearchChange?: (value: string) => void;
};

export default function AiHubTableToolbar({
  title,
  exportTooltip = "Export",
  emptyExportTooltip = "Select rows in the table before exporting.",
  hasExportSelection = true,
  exportFailureMessage = "Export failed. Please try again.",
  onExport,
  showExport = true,
  objectIdSearch,
  onObjectIdSearchChange,
}: AiHubTableToolbarProps) {
  const { primaryColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  const [exporting, setExporting] = useState(false);

  const featureHoverBg = alpha(primaryColor, 0.04);

  const featureButtonSx = useMemo(
    () => ({
      color: `${primaryColor} !important`,
      textTransform: "none",
      fontWeight: 500,
      "& .MuiSvgIcon-root": { color: `${primaryColor} !important` },
      "& .MuiButton-startIcon .MuiSvgIcon-root": {
        color: `${primaryColor} !important`,
      },
    }),
    [primaryColor],
  );

  const exportButtonSx = useMemo(
    () => ({
      color: "#595959 !important",
      textTransform: "none",
      fontWeight: 500,
      "&:hover, &:active": {
        backgroundColor: featureHoverBg,
      },
      "& .MuiSvgIcon-root": {
        color: `${primaryColor} !important`,
      },
      "& .MuiButton-startIcon .MuiSvgIcon-root": {
        color: `${primaryColor} !important`,
      },
      "&.Mui-disabled": {
        pointerEvents: "auto",
        color: "#595959 !important",
        "&:hover, &:active": {
          backgroundColor: featureHoverBg,
        },
        "& .MuiSvgIcon-root": {
          color: `${primaryColor} !important`,
        },
        "& .MuiButton-startIcon .MuiSvgIcon-root": {
          color: `${primaryColor} !important`,
        },
      },
    }),
    [featureHoverBg, primaryColor],
  );

  const featureButtonSlotProps = useMemo(
    () => ({
      button: {
        size: "small" as const,
        sx: {
          ...featureButtonSx,
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26) !important",
            "& .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
            "& .MuiButton-startIcon .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
          },
        },
      },
    }),
    [featureButtonSx],
  );

  const [objectIdInput, setObjectIdInput] = useState(objectIdSearch ?? "");

  useEffect(() => {
    setObjectIdInput(objectIdSearch ?? "");
  }, [objectIdSearch]);

  const debouncedObjectIdSearch = useMemo(
    () =>
      onObjectIdSearchChange
        ? debounce(onObjectIdSearchChange, OBJECT_ID_SEARCH_DEBOUNCE_MS)
        : null,
    [onObjectIdSearchChange],
  );

  useEffect(
    () => () => {
      debouncedObjectIdSearch?.cancel();
    },
    [debouncedObjectIdSearch],
  );

  const handleObjectIdInputChange = useCallback(
    (value: string) => {
      setObjectIdInput(value);
      debouncedObjectIdSearch?.(value);
    },
    [debouncedObjectIdSearch],
  );

  const handleClearObjectIdSearch = useCallback(() => {
    debouncedObjectIdSearch?.cancel();
    setObjectIdInput("");
    onObjectIdSearchChange?.("");
  }, [debouncedObjectIdSearch, onObjectIdSearchChange]);

  const handleExport = useCallback(() => {
    if (!onExport || exporting || !hasExportSelection) return;
    setExporting(true);
    void Promise.resolve(onExport())
      .then(() => {
        showMessage("Export started. Check your downloads.", "success");
      })
      .catch((error: unknown) => {
        showMessage(
          error instanceof Error ? error.message : exportFailureMessage,
          "error",
        );
      })
      .finally(() => {
        setExporting(false);
      });
  }, [exportFailureMessage, exporting, hasExportSelection, onExport, showMessage]);

  return (
    <Box
      role="toolbar"
      sx={{
        p: "12px 0",
        bgcolor: "background.paper",
        boxShadow: "none",
        border: "none",
        borderBottom: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          flex: 1,
        }}
      >
        {onObjectIdSearchChange ? (
          <TextField
            variant="standard"
            value={objectIdInput}
            onChange={(event) => handleObjectIdInputChange(event.target.value)}
            placeholder="Search object_id..."
            sx={{
              width: { xs: "100%", sm: 280 },
              maxWidth: 360,
              "& .MuiInput-root": {
                fontSize: "14px",
                color: "#595959",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#bfbfbf",
                opacity: 1,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "rgba(0, 0, 0, 0.23)",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "rgba(0, 0, 0, 0.42)",
              },
            }}
            slotProps={{
              htmlInput: {
                "aria-label": "Search object_id",
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.75 }}>
                    <SearchIcon sx={{ fontSize: 18, color: "#bfbfbf" }} />
                  </InputAdornment>
                ),
                endAdornment: objectIdInput ? (
                  <InputAdornment position="end" sx={{ ml: 0.5 }}>
                    <IconButton
                      aria-label="Clear Search"
                      size="small"
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={handleClearObjectIdSearch}
                      sx={{ p: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
          />
        ) : title ? (
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#262626", fontSize: "16px" }}
          >
            {title}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          "& .MuiButton-root": {
            color: "#595959 !important",
            textTransform: "none",
            fontWeight: 500,
          },
          "& .MuiButton-startIcon .MuiSvgIcon-root": {
            color: "inherit !important",
          },
        }}
      >
        <GridToolbarDensitySelector slotProps={featureButtonSlotProps} />
        <GridToolbarColumnsButton slotProps={featureButtonSlotProps} />
        {showExport ? (
          <Tooltip
            title={
              exporting
                ? "Exporting…"
                : hasExportSelection
                  ? exportTooltip
                  : emptyExportTooltip
            }
            arrow
            placement="top"
          >
            <Box component="span" sx={{ display: "inline-flex" }}>
              <Button
                size="small"
                disabled={exporting || !hasExportSelection}
                onClick={handleExport}
                sx={exportButtonSx}
                startIcon={
                  <FileDownloadOutlinedIcon
                    sx={{ fontSize: "18px !important" }}
                  />
                }
              >
                {exporting ? "Exporting…" : "Export"}
              </Button>
            </Box>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
}
